import { useState, useEffect, useRef, useCallback } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTheme } from '@/hooks/useTheme'
import { useAuthStore } from '@stores/useAuthStore'
import useInfluencerProfile from '@/hooks/useInfluencerProfile'
import { useIsAdmin } from '@/hooks/useIsAdmin'
import { Logo } from '@/components/common/Logo'
import { api } from '@/lib/api'

const navLinks = [
  { path: '/', label: 'Home', exact: true },
  { path: '/courses', label: 'Courses' },
  { path: '/pricing', label: 'Pricing' },
  { path: '/my-learning', label: 'My Learning', authRequired: true },
]

interface BookmarkItem {
  id: string
  moduleId: string
  moduleTitle?: string
  courseId?: string
  courseTitle?: string
}

function useClickOutside(ref: React.RefObject<HTMLElement | null>, onClose: () => void) {
  useEffect(() => {
    function handle(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [ref, onClose])
}

export function Navbar() {
  const { pathname } = useLocation()
  const { theme, setTheme } = useTheme()
  const [mobileOpen, setMobileOpen] = useState(false)
  const { user, loading, initializeAuth, signOut } = useAuthStore()
  const { isInfluencer } = useInfluencerProfile()
  const isAdmin = useIsAdmin()

  const [showBookmarks, setShowBookmarks] = useState(false)
  const [bookmarks, setBookmarks] = useState<BookmarkItem[]>([])
  const [loadingBookmarks, setLoadingBookmarks] = useState(false)

  const bookmarksRef = useRef<HTMLDivElement>(null)
  useClickOutside(bookmarksRef, () => setShowBookmarks(false))

  useEffect(() => {
    initializeAuth()
  }, [initializeAuth])

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : theme === 'light' ? 'system' : 'dark')
  }

  const isActive = (link: typeof navLinks[number]) =>
    'exact' in link && link.exact
      ? pathname === link.path
      : pathname === link.path || pathname.startsWith(link.path + '/')

  const loadBookmarks = useCallback(async () => {
    if (!localStorage.getItem('accessToken')) return
    setLoadingBookmarks(true)
    try {
      const list = (await api.getBookmarks()) as BookmarkItem[]
      setBookmarks(list)
    } catch { /* silent */ }
    setLoadingBookmarks(false)
  }, [])

  const handleToggleBookmarks = () => {
    if (!showBookmarks) loadBookmarks()
    setShowBookmarks(!showBookmarks)
  }

  return (
    <header className="sticky top-0 z-40 w-full bg-[var(--color-surface-0)] shadow-[0_1px_0_var(--color-border-default)]">
      <nav className="container-main h-[72px] flex items-center justify-between">
        {/* Logo */}
        <div className="shrink-0">
          <Logo />
        </div>

        {/* Center: Desktop Nav */}
        <div className="hidden lg:flex items-center gap-1">
          {navLinks
            .filter((link) => !('authRequired' in link && link.authRequired) || !!user)
            .map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`group relative px-3 py-5 text-[14px] font-medium transition-colors ${
                isActive(link)
                  ? 'text-[var(--color-text-primary)]'
                  : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]'
              }`}
            >
              {link.label}
              <span className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-[2px] bg-[#f97316] rounded-full transition-all duration-300 ease-out ${
                isActive(link) ? 'w-4/5' : 'w-0 group-hover:w-4/5'
              }`} />
            </Link>
          ))}
          {/* Contextual links — only shown when applicable. */}
          {!!user && isInfluencer && (
            <Link
              to="/influencer/dashboard"
              className={`group relative px-3 py-5 text-[14px] font-medium transition-colors ${
                pathname.startsWith('/influencer')
                  ? 'text-[var(--color-text-primary)]'
                  : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]'
              }`}
            >
              Creator
              <span className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-[2px] bg-[#f97316] rounded-full transition-all duration-300 ease-out ${
                pathname.startsWith('/influencer') ? 'w-4/5' : 'w-0 group-hover:w-4/5'
              }`} />
            </Link>
          )}
          {!!user && isAdmin && (
            <Link
              to="/admin"
              className={`group relative px-3 py-5 text-[14px] font-medium transition-colors ${
                pathname.startsWith('/admin')
                  ? 'text-[var(--color-text-primary)]'
                  : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]'
              }`}
            >
              Admin
              <span className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-[2px] bg-[#f97316] rounded-full transition-all duration-300 ease-out ${
                pathname.startsWith('/admin') ? 'w-4/5' : 'w-0 group-hover:w-4/5'
              }`} />
            </Link>
          )}
        </div>

        {/* Right actions */}
        <div className="flex items-center gap-1.5">
          {/* Search */}
          <button
            onClick={() => document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }))}
            className="p-2 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-surface-2)] rounded-[var(--radius-sm)] transition-colors"
            aria-label="Search"
          >
            <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </button>

          {/* Bookmarks (logged in only) */}
          {!loading && user && (
            <div className="relative" ref={bookmarksRef}>
              <button
                onClick={handleToggleBookmarks}
                className={`p-2 rounded-[var(--radius-sm)] transition-colors ${showBookmarks ? 'text-[var(--color-brand-500)] bg-[var(--color-surface-2)]' : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-surface-2)]'}`}
                aria-label="Bookmarks"
              >
                <svg className="w-[18px] h-[18px]" fill={showBookmarks ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0z" />
                </svg>
              </button>
              {showBookmarks && (
                <div className="absolute right-0 top-full mt-2 w-80 max-h-96 bg-[var(--color-surface-1)] border border-[var(--color-border-default)] rounded-[var(--radius-lg)] shadow-xl overflow-hidden z-50">
                  <div className="px-4 py-3 border-b border-[var(--color-border-default)] flex items-center justify-between">
                    <span className="text-[13px] font-semibold text-white">Bookmarks</span>
                    <span className="text-[11px] text-[var(--color-text-muted)]">{bookmarks.length} saved</span>
                  </div>
                  <div className="overflow-y-auto max-h-72">
                    {loadingBookmarks ? (
                      <div className="p-6 text-center text-[13px] text-[var(--color-text-muted)]">Loading...</div>
                    ) : bookmarks.length === 0 ? (
                      <div className="p-6 text-center text-[13px] text-[var(--color-text-muted)]">No bookmarks yet. Bookmark modules while reading.</div>
                    ) : (
                      bookmarks.map((b) => (
                        <Link
                          key={b.id}
                          to={`/courses/${b.courseId}/${b.moduleId}`}
                          onClick={() => setShowBookmarks(false)}
                          className="flex flex-col px-4 py-3 hover:bg-[var(--color-surface-2)] transition-colors border-b border-[var(--color-border-default)] last:border-0"
                        >
                          <span className="text-[13px] text-[var(--color-text-primary)] font-medium truncate">{b.moduleTitle || b.moduleId}</span>
                          <span className="text-[11px] text-[var(--color-text-muted)] truncate">{b.courseTitle || b.courseId}</span>
                        </Link>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Theme toggle */}
          <button
            onClick={toggleTheme}
            className="p-2 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-surface-2)] rounded-[var(--radius-sm)] transition-colors"
            aria-label={`Theme: ${theme}`}
          >
            {theme === 'dark' ? (
              <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" /></svg>
            ) : theme === 'light' ? (
              <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" /></svg>
            ) : (
              <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0V12a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 12V5.25" /></svg>
            )}
          </button>

          {/* Auth */}
          {!loading && (
            user ? (
              <div className="hidden sm:flex items-center gap-2 ml-2">
                <Link
                  to="/profile"
                  className="flex items-center gap-2 px-3 py-1.5 rounded-[var(--radius-md)] hover:bg-[var(--color-surface-2)] transition-colors"
                >
                  <div className="w-7 h-7 rounded-full bg-[var(--color-brand-500)] flex items-center justify-center text-[12px] font-bold text-[#111]">
                    {(user.displayName || user.email || '?')[0].toUpperCase()}
                  </div>
                  <span className="text-[13px] text-[var(--color-text-secondary)] max-w-[120px] truncate">
                    {user.displayName || user.email}
                  </span>
                </Link>
                <button
                  onClick={() => signOut()}
                  className="p-2 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-surface-2)] rounded-[var(--radius-sm)] transition-colors"
                  aria-label="Sign out"
                >
                  <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
                  </svg>
                </button>
              </div>
            ) : (
              <a
                href="/login"
                className="hidden sm:inline-flex items-center gap-2 ml-2 px-5 py-2 text-[13px] font-semibold text-[#111] rounded-[var(--radius-md)] bg-[var(--color-brand-500)] hover:bg-[var(--color-brand-400)] transition-colors"
              >
                Sign In
              </a>
            )
          )}

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden p-2 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-surface-2)] rounded-[var(--radius-sm)] transition-colors ml-1"
            aria-label="Menu"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              {mobileOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
              )}
            </svg>
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-[var(--color-border-default)] bg-[var(--color-surface-0)]">
          <div className="container-main py-3 flex flex-col gap-0.5">
            {navLinks
              .filter((link) => !('authRequired' in link && link.authRequired) || !!user)
              .map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileOpen(false)}
                className={`px-3 py-2.5 text-[15px] font-medium rounded-[var(--radius-sm)] transition-colors ${
                  isActive(link)
                    ? 'text-white bg-[var(--color-surface-2)]'
                    : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-surface-2)]'
                }`}
              >
                {link.label}
              </Link>
            ))}
            {!!user && isInfluencer && (
              <Link
                to="/influencer/dashboard"
                onClick={() => setMobileOpen(false)}
                className={`px-3 py-2.5 text-[15px] font-medium rounded-[var(--radius-sm)] transition-colors ${
                  pathname.startsWith('/influencer')
                    ? 'text-white bg-[var(--color-surface-2)]'
                    : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-surface-2)]'
                }`}
              >
                Creator Dashboard
              </Link>
            )}
            {!!user && isAdmin && (
              <Link
                to="/admin"
                onClick={() => setMobileOpen(false)}
                className={`px-3 py-2.5 text-[15px] font-medium rounded-[var(--radius-sm)] transition-colors ${
                  pathname.startsWith('/admin')
                    ? 'text-white bg-[var(--color-surface-2)]'
                    : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-surface-2)]'
                }`}
              >
                Admin
              </Link>
            )}
            {!loading && (
              user ? (
                <>
                  <Link
                    to="/profile"
                    onClick={() => setMobileOpen(false)}
                    className="mt-2 flex items-center gap-3 px-3 py-2.5 rounded-[var(--radius-sm)] hover:bg-[var(--color-surface-2)] transition-colors"
                  >
                    <div className="w-7 h-7 rounded-full bg-[var(--color-brand-500)] flex items-center justify-center text-[12px] font-bold text-[#111]">
                      {(user.displayName || user.email || '?')[0].toUpperCase()}
                    </div>
                    <span className="text-[15px] text-[var(--color-text-secondary)]">{user.displayName || user.email}</span>
                  </Link>
                  <button
                    onClick={() => { signOut(); setMobileOpen(false) }}
                    className="mt-1 px-3 py-2.5 text-[15px] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] text-left rounded-[var(--radius-sm)] hover:bg-[var(--color-surface-2)] transition-colors"
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <a
                  href="/login"
                  onClick={() => setMobileOpen(false)}
                  className="mt-2 px-3 py-2.5 text-[15px] font-semibold text-[#111] rounded-[var(--radius-md)] text-center bg-[var(--color-brand-500)] block"
                >
                  Sign In
                </a>
              )
            )}
          </div>
        </div>
      )}
    </header>
  )
}
